import React, { useContext, useEffect } from "react";
import { UserContext } from "components/user/UserContext";
import { Helmet } from "react-helmet-async";
import { Body, BodyHeading, BodyContent } from "@thrivetribe/mvf.ui.organisms.body";
import { Tab } from "types/tab";
import { Tabs } from "@thrivetribe/mvf.ui.molecules.tabs";
import { useLazyStatsApiQuery } from "redux/statsApiSlice";
import { ClubStats as ClubStatsProp } from "types/Legacy/ClubStats";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";
import { FixturesTable, LatestResultsTable, LeagueTable, StatsFilterTable } from "ui/organisms/Tables";
import { useNavigate } from "react-router-dom";
import { IsArrayEmpty, IsValueEmpty } from "helpers/isEmpty";
import RtkErrorMessage from "ui/organisms/Errors/RtkErrorMessage";
import "./MyClub.scss";

interface StatsProp {
  stats: ClubStatsProp | undefined;
  error?: unknown | undefined;
}

const TabPanelLoading = () => {
  return (
    <div className="my-club__tab-panel__loading-container">
      <LoadingSpinner fullscreen={true} />
    </div>
  );
};

const LeagueStats = ({ stats }: StatsProp): JSX.Element => {
  return (
    <BodyContent>
      <h2>League Stats {stats?.League?.SeasonNumber ? ` | Season ${stats?.League?.SeasonNumber}` : ""}</h2>
      <section className="club-stats">
        <LeagueTable stats={stats} />
        <LatestResultsTable title="Latest Results" stats={stats?.ListLeagueFixtures} />
      </section>
      <h2>Filter by</h2>
      <section className="filter-by">
        <StatsFilterTable stats={stats} />
      </section>
    </BodyContent>
  );
};

const LeagueFixtures = ({ stats }: StatsProp): JSX.Element => {
  return (
    <BodyContent>
      <h2>Upcoming fixtures {stats?.League?.SeasonNumber ? ` | Season ${stats?.League?.SeasonNumber}` : ""}</h2>
      <section className="fixtures">
        <FixturesTable title="Club Fixtures" stats={stats?.ListLeagueFixtures} />
      </section>
    </BodyContent>
  );
};

const CupMatches = ({ stats }: StatsProp): JSX.Element => {
  return (
    <BodyContent>
      <h2>Cup matches {stats?.League?.SeasonNumber ? ` | Season ${stats?.League?.SeasonNumber}` : ""}</h2>
      <section className="cup-matches">
        <FixturesTable title="Upcoming Matches" stats={stats?.ListLeagueCupFixtures} />
        <LatestResultsTable title="Latest Match Results" stats={stats?.ListLeagueCupFixtures} />
      </section>
    </BodyContent>
  );
};

export const MyClubPage = (): React.ReactElement => {
  const context = useContext(UserContext);
  const [trigger, { data: stats, isLoading, isError, error }] = useLazyStatsApiQuery();
  const navigate = useNavigate();

  useEffect(() => {
    if (context) {
      if (IsValueEmpty(context.clubTag)) {
        // errorCase: if there is no culbTag, navigate to no-club page
        !isLoading && navigate("/error/no-club-found");
      } else {
        // trigger api call if only clubTag exists
        trigger(context.clubTag);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, trigger, navigate]);

  useEffect(() => {
    if (stats && stats.ListLeagueFixtures && stats.LeagueFixtureStats_WeightGoal) {
      if (IsArrayEmpty(stats.ListLeagueFixtures) || IsArrayEmpty(stats.LeagueFixtureStats_WeightGoal)) {
        // errorCase: if there is no data in stats, navigate to no-club page
        navigate("/error/no-club-found");
      }
    }
  }, [navigate, stats]);

  const tabs: Tab[] = [
    {
      title: "League stats",
      children: <LeagueStats stats={stats} />,
    },
    {
      title: "League fixtures",
      children: <LeagueFixtures stats={stats} />,
    },
  ];

  if (stats?.ListLeagueCupFixtures && stats.ListLeagueCupFixtures.length > 0) {
    tabs.push({
      title: "Cup matches",
      children: <CupMatches stats={stats} />,
    });
  }

  if (isError) {
    // errorCase: if there is an api error, render error message
    return <RtkErrorMessage error={error} renderNav={true} />;
  }

  return (
    <Body className="my-club">
      <Helmet>
        <title>My Club - MAN v FAT</title>
      </Helmet>
      <BodyHeading>
        <h1>My Club</h1>
      </BodyHeading>
      {isLoading ? (
        <TabPanelLoading />
      ) : (
        <Tabs
          tabs={tabs}
          classNames={{
            container: "my-club__tabs",
            listContainer: "my-club__list-container",
            list: "my-club__list",
            listItem: "my-club__list-item",
            listItemText: "my-club__list-item__text",
            tabPanel: "my-club__tab-panel",
          }}
        />
      )}
    </Body>
  );
};
